import React, { useState, useEffect } from 'react';
import { Star, MessageSquare, ShieldCheck, Calendar } from 'lucide-react';
import { ReviewProvider, useReviewContext } from '../../context/ReviewContext';
import ReviewReplySection from '../../components/ReviewReplySection';

const UserReviewsContent = () => {
  const { reviews, loading, error, getReviews } = useReviewContext();
  const [filterRating, setFilterRating] = useState(0);
  const [authError, setAuthError] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token_partner_activities');
    const id = localStorage.getItem('id_partner_activities');

    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const businessId = payload.business_id || id;
        if (businessId) {
          getReviews('Task', businessId, token);
        } else {
          setAuthError('Business identifier not found');
        }
      } catch (e) {
        setAuthError('Error parsing authentication info');
      }
    } else {
      setAuthError('Authentication required');
    }
  }, [getReviews]);

  const stats = {
    total: reviews.length,
    average: (reviews.reduce((acc, r) => acc + r.rating, 0) / reviews.length || 0).toFixed(1),
    positive: reviews.filter(r => r.rating >= 4).length
  };

  const filteredReviews = filterRating 
    ? reviews.filter(r => r.rating === filterRating) 
    : reviews;

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-96 space-y-4">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        <p className="text-gray-500 font-black uppercase tracking-widest text-[10px] animate-pulse">Checking Guest Feedback...</p>
      </div>
    );
  }

  if (authError || error) {
    return (
      <div className="p-12 text-center text-rose-500 font-bold">
        {authError || error}
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-in fade-in duration-700">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-gray-900 tracking-tight uppercase">Guest Feedback</h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">Track and respond to customer experiences.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-6 rounded-[2rem] border border-gray-100 shadow-sm flex flex-col items-center text-center">
          <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5">Overall Rating</p>
          <p className="text-4xl font-black text-gray-900 tracking-tighter mb-2">{stats.average}</p>
          <div className="flex gap-0.5 mb-2">
            {[1, 2, 3, 4, 5].map(s => (
              <Star key={s} size={16} className={s <= Math.round(stats.average) ? 'text-amber-400 fill-amber-400' : 'text-gray-100'} />
            ))}
          </div>
          <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Based on {stats.total} reviews</p>
        </div>

        <div className="bg-primary p-6 rounded-[2rem] shadow-lg shadow-primary/10 flex flex-col items-center text-center justify-center">
          <p className="text-3xl font-black text-white tracking-tighter">{stats.positive}</p>
          <p className="text-[10px] font-black text-white/60 uppercase tracking-widest mt-1">Verified Positivity</p>
        </div>

        <div className="bg-white p-6 rounded-[2rem] border border-gray-100 shadow-sm flex flex-col items-center text-center justify-center">
           <MessageSquare size={32} className="text-primary/10 mb-2" />
           <p className="text-[10px] font-black text-gray-900 uppercase tracking-widest">Community Hub</p>
        </div>
      </div>

      <div className="bg-white p-4 rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="flex items-center gap-2 px-2 overflow-x-auto custom-scrollbar">
          <button onClick={() => setFilterRating(0)} className={`px-4 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all whitespace-nowrap ${filterRating === 0 ? 'bg-primary text-white shadow-md shadow-primary/10' : 'bg-gray-50 text-gray-400 hover:bg-gray-100'}`}>All Reviews</button>
          {[5, 4, 3, 2, 1].map(r => (
            <button key={r} onClick={() => setFilterRating(r)} className={`px-4 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all whitespace-nowrap flex items-center gap-1.5 ${filterRating === r ? 'bg-primary text-white shadow-md shadow-primary/10' : 'bg-gray-50 text-gray-400 hover:bg-gray-100'}`}>
              {r} <Star size={10} className={filterRating === r ? 'fill-white' : 'fill-current'} />
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-4">
        {filteredReviews.length > 0 ? (
          filteredReviews.map((review) => {
            const guestName = review.booking_id?.name || review.user_id?.name || 'Verified Traveler';
            return (
              <div key={review._id} className="bg-white p-6 rounded-[2rem] border border-gray-100 shadow-sm hover:shadow-md transition-all group space-y-4">
                <div className="flex flex-col md:flex-row gap-5">
                  <div className="w-12 h-12 rounded-xl bg-gray-50 border border-gray-100 flex items-center justify-center text-gray-400 font-black text-xl uppercase shrink-0">
                     {guestName.charAt(0)}
                  </div>
                  <div className="flex-1 space-y-3">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-2">
                      <div>
                        <h4 className="font-black text-gray-900 text-base uppercase tracking-tight">{guestName}</h4>
                        <div className="flex items-center gap-3 mt-0.5">
                          <div className="flex gap-0.5">
                             {[1,2,3,4,5].map(s => <Star key={s} size={12} className={s <= review.rating ? 'text-amber-400 fill-amber-400' : 'text-gray-200'} />)}
                          </div>
                          <span className="text-[10px] text-gray-400 font-black uppercase tracking-widest flex items-center gap-1.5"><Calendar size={10} /> {new Date(review.createdAt).toLocaleDateString()}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 px-3 py-1 bg-emerald-50 rounded-lg text-emerald-600 font-black text-[9px] uppercase tracking-widest h-fit border border-emerald-100/50">
                         <ShieldCheck size={12} /> Verified
                      </div>
                    </div>
                    {review.title && <h5 className="font-black text-sm text-gray-800 uppercase">{review.title}</h5>}
                    <p className="text-gray-600 font-bold text-sm leading-relaxed">{review.description || review.comment}</p>
                  </div>
                </div>

                <ReviewReplySection review={review} tokenStorageKey="token_partner_activities" />
              </div>
            );
          })
        ) : (
          <div className="flex flex-col items-center justify-center py-32 px-10 text-center">
            <div className="w-24 h-24 bg-gray-50 rounded-full flex items-center justify-center text-gray-200 mb-8"><MessageSquare size={48} /></div>
            <h3 className="text-2xl font-black text-gray-900 tracking-tight uppercase">No Feedback Yet</h3>
            <p className="text-[10px] font-black text-gray-400 mt-2 uppercase tracking-widest max-w-[240px]">Reviews will appear here once guest experiences are completed.</p>
          </div>
        )}
      </div>
    </div>
  );
};

const UserReviews = () => (
  <ReviewProvider>
    <UserReviewsContent />
  </ReviewProvider>
);

export default UserReviews;
